import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Sparkles, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";

const MEDALS = ["🥇", "🥈", "🥉"];

/**
 * Top AI Daters leaderboard widget.
 * Renders the top public AI characters by gifts + tips received.
 * Auto-hides when there's no data (early days / cold start).
 */
export default function TopAIDatersLeaderboard({ window: timeWindow = "week", limit = 5 }) {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(timeWindow);

  const fetchLeaders = useCallback(async (win) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ai-dating/leaderboard?window=${win}&limit=${limit}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setLeaders(data.leaders || []);
      }
    } catch {
      // optional widget — silent fail
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetchLeaders(tab); }, [fetchLeaders, tab]);

  // Hide entirely if there's no data — don't show a sad empty widget
  if (!loading && leaders.length === 0) return null;

  return (
    <div className="brutal-card p-3 mb-4" data-testid="top-ai-daters-leaderboard">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-[#FFD700] flex items-center gap-1">
          <Crown className="w-4 h-4" />
          Top AI Daters
        </h3>
        <div className="flex gap-1">
          <button
            onClick={() => setTab("week")}
            className={`text-[10px] px-2 py-0.5 border-2 ${tab === "week" ? "border-[#FFD700] text-[#FFD700]" : "border-[#6600CC] text-[#FF66FF]"}`}
            data-testid="leaderboard-tab-week"
          >
            This Week
          </button>
          <button
            onClick={() => setTab("all")}
            className={`text-[10px] px-2 py-0.5 border-2 ${tab === "all" ? "border-[#FFD700] text-[#FFD700]" : "border-[#6600CC] text-[#FF66FF]"}`}
            data-testid="leaderboard-tab-all"
          >
            All Time
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#FF66FF]">Loading…</div>
      ) : (
        <ol className="space-y-1">
          {leaders.map((l, idx) => {
            const avatar = l.avatar_url
              ? (l.avatar_url.startsWith("http") ? l.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${l.avatar_url}`)
              : null;
            return (
              <motion.li
                key={l.character_id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04 }}
                onClick={() => navigate(`/ai-dating/chat/${l.character_id}`)}
                className="flex items-center gap-2 p-1.5 cursor-pointer hover:bg-[rgba(255,215,0,0.1)] border border-transparent hover:border-[#FFD700]"
                data-testid={`leaderboard-row-${idx + 1}`}
              >
                <span className="w-6 text-center text-base flex-shrink-0">
                  {MEDALS[idx] || <span className="text-[#FF66FF] text-xs">#{idx + 1}</span>}
                </span>
                <div className="w-8 h-8 border-2 border-[#FF00FF] bg-[#000033] overflow-hidden flex-shrink-0">
                  {avatar ? (
                    <img src={avatar} alt={l.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#FF00FF]">
                      <Sparkles className="w-3 h-3" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#00FFFF] truncate">{l.name}</p>
                  <p className="text-[10px] text-[#888] truncate">by {l.owner_name || "anon"}</p>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <p className="text-xs font-bold text-[#FFD700] flex items-center gap-0.5">
                    <Trophy className="w-3 h-3" />
                    {l.score}
                  </p>
                  <p className="text-[9px] text-[#FF66FF]">
                    {l.gift_count > 0 && `🎁 ${l.gift_count}`}
                    {l.gift_count > 0 && l.tip_count > 0 && " · "}
                    {l.tip_count > 0 && `💸 ${l.tip_count}`}
                  </p>
                </div>
              </motion.li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
